'use client';

import React, { useState } from 'react';
import { Clock, Truck, CheckCircle, ChevronRight, Package, User, Calendar, Eye, AlertTriangle, RefreshCw, Search, ArrowRight,  } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import Modal from '@/components/ui/Modal';
import { toast } from 'sonner';

type FulfillmentStatus = 'Pending_Review' | 'Loading' | 'Delivered_Synced';
type PaymentMethod = 'Cash' | 'Deferred_Credit' | 'Bank_Card' | 'Cheque' | 'Mobile_Wallet';

interface KanbanCard {
  id: string;
  invoice_number: string;
  retailer_name: string;
  retailer_zone: string;
  total_amount: number;
  payment_method: PaymentMethod;
  item_count: number;
  created_at: string;
  fulfillment_status: FulfillmentStatus;
  notes?: string;
  is_urgent?: boolean;
}

// Mock data — backend: SELECT * FROM invoices_bons_livraison WHERE supplier_id = auth.uid() ORDER BY created_at DESC
const initialCards: KanbanCard[] = [
  { id: 'inv-001', invoice_number: 'BL-2026-0841', retailer_name: 'Snack Atlas Guéliz', retailer_zone: 'Centre_Ville', total_amount: 1240.00, payment_method: 'Deferred_Credit', item_count: 5, created_at: '26/05/2026 09:12', fulfillment_status: 'Pending_Review', is_urgent: true },
  { id: 'inv-002', invoice_number: 'BL-2026-0840', retailer_name: 'Restaurant Fès Marrakech', retailer_zone: 'Ville_Haute', total_amount: 2650.50, payment_method: 'Cash', item_count: 8, created_at: '26/05/2026 08:45', fulfillment_status: 'Pending_Review', notes: 'Livraison avant 12h' },
  { id: 'inv-003', invoice_number: 'BL-2026-0839', retailer_name: 'Café Majorelle', retailer_zone: 'Centre_Ville', total_amount: 890.00, payment_method: 'Bank_Card', item_count: 3, created_at: '26/05/2026 08:20', fulfillment_status: 'Pending_Review' },
  { id: 'inv-004', invoice_number: 'BL-2026-0838', retailer_name: 'Épicerie Bab Jdid', retailer_zone: 'Khwazat', total_amount: 3180.75, payment_method: 'Cheque', item_count: 12, created_at: '26/05/2026 07:55', fulfillment_status: 'Pending_Review' },
  { id: 'inv-005', invoice_number: 'BL-2026-0837', retailer_name: 'Boulangerie Menara', retailer_zone: 'Ville_Haute', total_amount: 1560.00, payment_method: 'Deferred_Credit', item_count: 6, created_at: '25/05/2026 17:30', fulfillment_status: 'Loading', is_urgent: true },
  { id: 'inv-006', invoice_number: 'BL-2026-0836', retailer_name: 'Café Bab Doukkala', retailer_zone: 'Centre_Ville', total_amount: 720.50, payment_method: 'Mobile_Wallet', item_count: 4, created_at: '25/05/2026 16:10', fulfillment_status: 'Loading' },
  { id: 'inv-007', invoice_number: 'BL-2026-0835', retailer_name: 'Snack Atlas Guéliz', retailer_zone: 'Centre_Ville', total_amount: 980.00, payment_method: 'Cash', item_count: 5, created_at: '25/05/2026 14:45', fulfillment_status: 'Loading', notes: 'Produits frais — priorité' },
  { id: 'inv-008', invoice_number: 'BL-2026-0834', retailer_name: 'Restaurant Fès Marrakech', retailer_zone: 'Ville_Haute', total_amount: 4200.00, payment_method: 'Bank_Card', item_count: 15, created_at: '25/05/2026 11:00', fulfillment_status: 'Delivered_Synced' },
  { id: 'inv-009', invoice_number: 'BL-2026-0833', retailer_name: 'Boulangerie Menara', retailer_zone: 'Ville_Haute', total_amount: 1850.00, payment_method: 'Deferred_Credit', item_count: 7, created_at: '25/05/2026 09:30', fulfillment_status: 'Delivered_Synced' },
  { id: 'inv-010', invoice_number: 'BL-2026-0832', retailer_name: 'Épicerie Bab Jdid', retailer_zone: 'Khwazat', total_amount: 3400.25, payment_method: 'Cheque', item_count: 11, created_at: '24/05/2026 16:00', fulfillment_status: 'Delivered_Synced' },
  { id: 'inv-011', invoice_number: 'BL-2026-0831', retailer_name: 'Café Majorelle', retailer_zone: 'Centre_Ville', total_amount: 640.00, payment_method: 'Cash', item_count: 3, created_at: '24/05/2026 14:20', fulfillment_status: 'Delivered_Synced' },
];

const columns: { status: FulfillmentStatus; label: string; icon: React.ElementType; color: string; bgColor: string }[] = [
  { status: 'Pending_Review', label: 'En Attente', icon: Clock, color: 'text-warning', bgColor: 'bg-warning-light border-warning/30' },
  { status: 'Loading', label: 'En Chargement', icon: Truck, color: 'text-primary', bgColor: 'bg-primary-light border-primary/30' },
  { status: 'Delivered_Synced', label: 'Livré & Synchronisé', icon: CheckCircle, color: 'text-success', bgColor: 'bg-success-light border-success/30' },
];

const nextStatus: Record<FulfillmentStatus, FulfillmentStatus | null> = {
  Pending_Review: 'Loading',
  Loading: 'Delivered_Synced',
  Delivered_Synced: null,
};

const nextStatusLabel: Record<FulfillmentStatus, string> = {
  Pending_Review: 'Démarrer Chargement',
  Loading: 'Marquer Livré',
  Delivered_Synced: '',
};

export default function KanbanBoardContent() {
  const [cards, setCards] = useState<KanbanCard[]>(initialCards);
  const [selectedCard, setSelectedCard] = useState<KanbanCard | null>(null);
  const [confirmMove, setConfirmMove] = useState<{ card: KanbanCard; to: FulfillmentStatus } | null>(null);
  const [search, setSearch] = useState('');

  const moveCard = (card: KanbanCard, toStatus: FulfillmentStatus) => {
    // Backend: UPDATE invoices_bons_livraison SET fulfillment_status = toStatus, updated_at = NOW() WHERE id = card.id
    // If toStatus === 'Delivered_Synced': trigger DB tracking record INSERT
    setCards((prev) =>
      prev.map((c) => c.id === card.id ? { ...c, fulfillment_status: toStatus } : c)
    );
    toast.success(
      toStatus === 'Delivered_Synced'
        ? `Commande ${card.invoice_number} livrée — solde mis à jour`
        : `Commande ${card.invoice_number} → ${columns.find((col) => col.status === toStatus)?.label}`
    );
    setConfirmMove(null);
    setSelectedCard(null);
  };

  const filteredCards = (status: FulfillmentStatus) =>
    cards.filter((c) => c.fulfillment_status === status &&
      (search === '' || c.retailer_name.toLowerCase().includes(search.toLowerCase()) || c.invoice_number.includes(search))
    );

  const columnTotal = (status: FulfillmentStatus) =>
    cards.filter((c) => c.fulfillment_status === status).reduce((sum, c) => sum + c.total_amount, 0);

  return (
    <div className="space-y-5 fade-in">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <h2 className="text-xl font-700 text-foreground">Pipeline de Fulfillment</h2>
          <p className="text-sm text-muted-foreground">{cards.length} commandes au total · Mis à jour à 09:46</p>
        </div>
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Rechercher commande..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-base pl-9 w-56"
          />
        </div>
        <button
          onClick={() => toast('Données actualisées')}
          className="btn-secondary text-xs py-2 px-3"
        >
          <RefreshCw size={13} />
          Actualiser
        </button>
      </div>

      {/* Kanban columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {columns.map((col) => {
          const colCards = filteredCards(col.status);
          const total = columnTotal(col.status);
          const ColIcon = col.icon;

          return (
            <div key={`col-${col.status}`} className="flex flex-col min-h-[500px]">
              {/* Column header */}
              <div className={`flex items-center justify-between px-4 py-3 rounded-xl border mb-3 ${col.bgColor}`}>
                <div className="flex items-center gap-2">
                  <ColIcon size={16} className={col.color} />
                  <span className="text-sm font-700 text-foreground">{col.label}</span>
                  <span className={`text-xs font-700 px-2 py-0.5 rounded-full ${col.bgColor} ${col.color} border ${col.bgColor.replace('bg-', 'border-').split(' ')[0]}`}>
                    {colCards.length}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-xs font-700 text-foreground font-tabular">
                    {total.toLocaleString('fr-MA', { maximumFractionDigits: 0 })} MAD
                  </p>
                </div>
              </div>

              {/* Cards */}
              <div className="flex-1 space-y-3">
                {colCards.map((card) => (
                  <KanbanCardComponent
                    key={card.id}
                    card={card}
                    onView={() => setSelectedCard(card)}
                    onMove={() => {
                      const next = nextStatus[card.fulfillment_status];
                      if (next) setConfirmMove({ card, to: next });
                    }}
                  />
                ))}
                {colCards.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-10 border-2 border-dashed border-border rounded-xl text-center">
                    <ColIcon size={24} className="text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Aucune commande</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Card detail modal */}
      <Modal open={!!selectedCard} onClose={() => setSelectedCard(null)} title={`Commande ${selectedCard?.invoice_number}`} size="md">
        {selectedCard && (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'N° Facture', value: selectedCard.invoice_number, mono: true },
                { label: 'Détaillant', value: selectedCard.retailer_name },
                { label: 'Zone', value: selectedCard.retailer_zone.replace('_', ' ') },
                { label: 'Nombre d\'Articles', value: `${selectedCard.item_count} articles` },
                { label: 'Montant Total', value: `${selectedCard.total_amount.toFixed(2)} MAD` },
                { label: 'Mode de Paiement', value: selectedCard.payment_method.replace('_', ' ') },
                { label: 'Date de Commande', value: selectedCard.created_at },
                { label: 'Statut', value: selectedCard.fulfillment_status.replace('_', ' ') },
              ].map((row) => (
                <div key={`modal-detail-${row.label}`}>
                  <p className="text-2xs text-muted-foreground font-600 mb-0.5">{row.label}</p>
                  <p className={`text-sm font-700 text-foreground ${row.mono ? 'font-mono' : ''}`}>{row.value}</p>
                </div>
              ))}
            </div>
            {selectedCard.notes && (
              <div className="p-3 bg-warning-light border border-warning/30 rounded-xl">
                <p className="text-xs font-700 text-warning mb-0.5">Note</p>
                <p className="text-xs text-foreground">{selectedCard.notes}</p>
              </div>
            )}
            {selectedCard.is_urgent && (
              <div className="flex items-center gap-2 p-3 bg-danger-light border border-danger/30 rounded-xl">
                <AlertTriangle size={14} className="text-danger" />
                <p className="text-xs font-700 text-danger">Commande urgente — traitement prioritaire requis</p>
              </div>
            )}
            <div className="flex gap-3 pt-2 border-t border-border">
              <button onClick={() => setSelectedCard(null)} className="btn-secondary flex-1 justify-center">Fermer</button>
              {nextStatus[selectedCard.fulfillment_status] && (
                <button
                  onClick={() => {
                    const next = nextStatus[selectedCard.fulfillment_status];
                    if (next) setConfirmMove({ card: selectedCard, to: next });
                  }}
                  className="btn-primary flex-1 justify-center"
                >
                  <ArrowRight size={14} />
                  {nextStatusLabel[selectedCard.fulfillment_status]}
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Confirm move modal */}
      <Modal open={!!confirmMove} onClose={() => setConfirmMove(null)} title="Confirmer le Déplacement" size="sm">
        {confirmMove && (
          <div className="p-5">
            <p className="text-sm text-foreground mb-1">
              Déplacer <span className="font-700">{confirmMove.card.invoice_number}</span> vers{' '}
              <span className="font-700">{columns.find((c) => c.status === confirmMove.to)?.label}</span> ?
            </p>
            {confirmMove.to === 'Delivered_Synced' && (
              <p className="text-xs text-muted-foreground mt-2 p-3 bg-primary-light rounded-xl border border-primary/20">
                Cette action mettra à jour le solde du crédit du détaillant et créera un enregistrement de suivi automatique.
              </p>
            )}
            <div className="flex gap-3 mt-5">
              <button onClick={() => setConfirmMove(null)} className="btn-secondary flex-1 justify-center">Annuler</button>
              <button
                onClick={() => moveCard(confirmMove.card, confirmMove.to)}
                className="btn-primary flex-1 justify-center"
              >
                <CheckCircle size={14} />
                Confirmer
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function KanbanCardComponent({
  card,
  onView,
  onMove,
}: {
  card: KanbanCard;
  onView: () => void;
  onMove: () => void;
}) {
  const next = nextStatus[card.fulfillment_status];

  return (
    <div className={`card-base kanban-card-hover p-0 overflow-hidden ${card.is_urgent ? 'border-l-4 border-l-danger' : ''}`}>
      {/* Card header */}
      <div className="flex items-start justify-between px-4 pt-3 pb-2 border-b border-border">
        <div>
          <p className="text-xs font-700 text-primary font-mono">{card.invoice_number}</p>
          <p className="text-sm font-700 text-foreground mt-0.5">{card.retailer_name}</p>
        </div>
        <div className="flex items-center gap-1">
          {card.is_urgent && (
            <span className="text-2xs font-700 bg-danger-light text-danger px-1.5 py-0.5 rounded-full">Urgent</span>
          )}
          <button
            onClick={onView}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title="Voir les détails de la commande"
          >
            <Eye size={13} />
          </button>
        </div>
      </div>

      {/* Card body */}
      <div className="px-4 py-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Package size={12} />
            <span>{card.item_count} articles</span>
          </div>
          <p className="text-base font-800 text-foreground font-tabular">
            {card.total_amount.toLocaleString('fr-MA', { maximumFractionDigits: 2 })} MAD
          </p>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar size={12} />
            <span>{card.created_at}</span>
          </div>
          <StatusBadge status={card.payment_method} size="sm" />
        </div>

        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <User size={12} />
          <span>{card.retailer_zone.replace('_', ' ')}</span>
        </div>

        {card.notes && (
          <p className="text-2xs text-warning bg-warning-light px-2 py-1 rounded-lg font-600 truncate">
            📝 {card.notes}
          </p>
        )}
      </div>

      {/* Card footer — move action */}
      {next && (
        <div className="px-4 pb-3">
          <button
            onClick={onMove}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-700 bg-primary/10 text-primary hover:bg-primary hover:text-white transition-all duration-150"
          >
            <ChevronRight size={13} />
            {nextStatusLabel[card.fulfillment_status]}
          </button>
        </div>
      )}
      {!next && (
        <div className="px-4 pb-3">
          <div className="flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-700 bg-success-light text-success">
            <CheckCircle size={13} />
            Livré & Synchronisé
          </div>
        </div>
      )}
    </div>
  );
}